import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { assign, cloneDeep, each, find, first, round, sumBy } from 'lodash';
import { HttpService } from '../../services/http.service';
import { IOrder, IProduct } from '../../interfaces/IOrder';
import { ToastService } from 'angular-toastify';
import { finalize, lastValueFrom } from 'rxjs';
import { saveAs } from 'file-saver';
import { UserService } from '../../services/user.service';
import { OrderHistoryService } from '../../services/order-history.service';


// Factory function that returns the validator
function splitDiscountValidatorFactory(getFn: Function): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const parent = control.root as FormGroup;
    const splitDiscount = parent.get('splitDiscount');
    const amountReceived = parent.get('amountReceived');
    if (!splitDiscount?.value && amountReceived?.value > getFn()) {
      splitDiscount?.setErrors({ splitDiscountInvalid: true });
    } else {
      splitDiscount?.setErrors(null);
    }
    return null;
  };
}

@Component({
  selector: 'app-order-v2',
  templateUrl: './order-v2.component.html',
  styleUrls: ['./order-v2.component.css']
})
export class OrderV2Component implements OnChanges, OnInit {
  constructor(
    private readonly httpService: HttpService,
    private readonly toastService: ToastService,
    private readonly userService: UserService,
    private readonly orderHistoryService: OrderHistoryService,
  ) {
  }

  @Input()
  public order: IOrder | null = null;

  @Input()
  public orderId: string | null = null;

  public isButtonLoading = false;

  public currency: string = 'грн';

  public orderTotalPriceWithoutDiscount: number = 0

  public productsBackup: IProduct[] = [];

  public discount: number = 0

  public checkLink: string | null = null;

  public orderForm = new FormGroup({
    amountReceived: new FormControl(0, {
      nonNullable: true, validators: [
        this.orderAmountsValidator,
        splitDiscountValidatorFactory(() => {
          return this.orderTotalPriceWithoutDiscount;
        })
      ], updateOn: 'blur'
    }),
    splitDiscount: new FormControl(false, {
      nonNullable: true,
      validators: [
        splitDiscountValidatorFactory(() => {
          return this.orderTotalPriceWithoutDiscount;
        }),
      ],
      updateOn: 'change'
    }),
    paymentMethod: new FormControl('novaPay', { nonNullable: true }),
  });

  ngOnInit() {
    this.addFormSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['order'].currentValue && changes['order'].currentValue !== changes['order'].previousValue) {
      this.reset();
    }

    if (this.order) {
      this.discount = this.order?.discount || 0;
      this.orderTotalPriceWithoutDiscount = (this.order?.orderTotalPrice + this.discount) || 0
      this.orderForm?.get('amountReceived')?.setValue(this.order?.orderTotalPrice, { emitEvent: false });
      this.orderForm?.get('splitDiscount')?.setValue(false, { emitEvent: false });
      this.productsBackup = cloneDeep(this.order?.products);
    }
  }

  public async createCheck() {
    console.log('createCheck', {
      products: this.order?.products,
      discount: this.discount,
      orderIds: this.orderId,
      ...this.orderForm.getRawValue(),
    });
    this.isButtonLoading = true;
    this.httpService.createCheckV2({
      products: this.order?.products,
      discount: this.discount,
      orderIds: this.orderId,
      ...this.orderForm.getRawValue(),
    }).pipe(
      finalize(() => {
        this.isButtonLoading = false;
      })
    ).subscribe(async (data) => {
      this.checkLink = `https://check.checkbox.ua/${data?.id}`;
      await this.copyFileLink();
      this.orderHistoryService.updateList({
        orderIds: this.orderId as string,
        checkId: data.id,
        amountReceived: this.orderForm.getRawValue().amountReceived,
        createdAt: Date.now()
      });
    }, async () => {
      const { user } = await lastValueFrom(this.httpService.getUserProfile());
      this.userService.setUser(user);
    });
  }

  public async saveFile() {
    if (this.checkLink) {
      this.httpService.downloadFile(this.checkLink)
      .subscribe((blob) => {
        saveAs(blob, `Чек ${this.orderId}.pdf`);
      });
    }
  }

  public async copyFileLink() {
    if (this.checkLink) {
      await navigator.clipboard.writeText(this.checkLink);
      this.toastService.success('Адреса чеку скопійована в буфер');
    }
  }

  private addFormSubscriptions() {
    const amountReceivedControl = this.orderForm?.get('amountReceived') as FormControl;
    const splitDiscountControl = this.orderForm?.get('splitDiscount') as FormControl;

    amountReceivedControl.valueChanges.subscribe((amountReceivedValue: number) => {
      amountReceivedControl.setValue(round(amountReceivedValue, 2), { emitEvent: false });
      this.amountReceivedRecalculate(amountReceivedValue, splitDiscountControl.value)
    });
    splitDiscountControl.valueChanges.subscribe((splitDiscountValue) => {
      amountReceivedControl.setValue(round(amountReceivedControl.value, 2), { emitEvent: false });
      this.amountReceivedRecalculate(amountReceivedControl.value, splitDiscountValue)
    });
  }

  public onEnter($event: any) {
    $event.target.blur()
    this.orderForm.updateValueAndValidity();
  }

  private splitDiscountRecalculate(amountReceivedValue: number) {
    // пропорційно розбити знижку/націнку по товарах
    let diff = amountReceivedValue - this.orderTotalPriceWithoutDiscount;
    this.order?.products.forEach((product) => {
      this.calculateProductPrice(product, diff)
    });
    const sumTotalPrice = round(sumBy(this.order?.products, 'totalPrice'), 2);
    if (amountReceivedValue === sumTotalPrice) {
      return;
    }
    const totalPriceDiff = round(amountReceivedValue - sumTotalPrice, 2);
    // знайти продукт з quantity 1 і додати йому diff
    const targetSingleProduct = find(this.order?.products, { quantity: 1 });
    if (targetSingleProduct) {
      targetSingleProduct.price += totalPriceDiff;
      targetSingleProduct.totalPrice = round(targetSingleProduct.price * targetSingleProduct.quantity, 2);
      return;
    }
    // взяти перший продукт і розділити на 1 та х-1, додати diff до того, що 1
    const targetMultipleProduct = first(this.order?.products) as IProduct;
    targetMultipleProduct.quantity -= 1;
    targetMultipleProduct.totalPrice = round(targetMultipleProduct.price * targetMultipleProduct.quantity, 2);
    this.order?.products.push(assign({}, targetMultipleProduct, {
      quantity: 1,
      price: round(targetMultipleProduct.price + totalPriceDiff, 2),
      totalPrice: round(targetMultipleProduct.price + totalPriceDiff, 2)
    }));
  }

  private roundCents() {
    if(!this.order){
      return;
    }
    if(!this.order.products?.length){
      return;
    }
    if(this.order.products.length === 1){
      return;
    }
    let cents = 0;
    each(this.order.products, (product) => {
      const productCents = round(product.price % 1, 2);
      console.log('productCents', productCents);
      if(!productCents){
        return;
      }
      product.price = round(product.price - productCents, 2);
      product.totalPrice = round(product.quantity * product.price, 2);
      cents += productCents;
    })
    if(!cents){
      return;
    }
    const highestPriceProduct = this.order?.products.reduce((prev, current) => {
      return (prev.price > current.price) ? prev : current;
    });
    if(highestPriceProduct){
      highestPriceProduct.price = round(highestPriceProduct.price + cents, 2);
      highestPriceProduct.totalPrice = round(highestPriceProduct.quantity * highestPriceProduct.price, 2);
    }
  }

  private amountReceivedRecalculate(amountReceivedValue: number, splitDiscount: number) {
    this.discount = 0;
    if (this.order) {
      this.order.products = cloneDeep(this.productsBackup)
    }
    if (amountReceivedValue < this.orderTotalPriceWithoutDiscount) {
      // варіант 1
      if (!splitDiscount) {
        this.discount = round(this.orderTotalPriceWithoutDiscount - amountReceivedValue, 2);
      } else {
        this.splitDiscountRecalculate(amountReceivedValue);
        // this.roundCents();
      }

      return;
    }
    // варіант 2
    if (!splitDiscount) {
      return;
    }
    this.splitDiscountRecalculate(amountReceivedValue);
    // this.roundCents();
  }

  private calculateProductPrice(product: IProduct, diff: number) {
    const productDiff = round(product.price * 100 / this.orderTotalPriceWithoutDiscount * diff / 100, 2);
    product.price = round(product.price + productDiff, 2);
    product.totalPrice = round(product.price * product.quantity, 2);
  }

  private orderAmountsValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.root as FormGroup;
    const amountReceived = parent.get('amountReceived');
    if (amountReceived?.value < 0) {
      amountReceived?.setErrors({ orderAmountsInvalid: true });
    } else {
      amountReceived?.setErrors(null);
    }
    return null;
  }

  private reset() {
    this.checkLink = null;
    this.discount = this.order?.discount || 0;
  }
}
